import { useState,useEffect } from 'react';
import { Loader2,X,Search } from 'lucide-react';
import { api } from '@/utils/api';
import { type MappingItem,type PO,formatStatus } from './soPoMappingTypes';
export function AssignPoModal({
  mapping,
  onClose,
  onSaved,
}: {
  mapping: MappingItem;
  onClose: () => void;
  onSaved: (updated: MappingItem) => void;
}) {
  const [pos, setPos] = useState<PO[]>([]);
  const [search, setSearch] = useState('');
  const [selectedPoId, setSelectedPoId] = useState<string | null>(mapping.poId);
  const [status, setStatus] = useState<'unmapped' | 'in_progress' | 'mapped'>(mapping.status);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get('/purchase-orders?limit=100')
      .then(res => {
        const raw = res.data.data?.purchaseOrders ?? res.data.data?.items ?? res.data.data ?? [];
        setPos(raw);
      })
      .catch(() => {});
  }, []);

  const filtered = pos.filter(p =>
    p.number?.toLowerCase().includes(search.toLowerCase()) ||
    p.vendor?.name?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api.patch(`/so-po-mapping/${mapping.id}`, {
        purchaseOrderId: selectedPoId,
        status,
      });
      const raw = res.data.data;
      const po = pos.find(p => p.id === selectedPoId);
      onSaved({
        ...mapping,
        status: raw.status,
        poId: raw.purchaseOrderId ?? null,
        poNumber: po?.number ?? mapping.poNumber,
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update mapping');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-2xl p-6 shadow-2xl"
        style={{ background: 'var(--bg-surface)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Assign Purchase Order</h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{mapping.soNumber} — {mapping.itemDescription}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:opacity-70" style={{ background: 'var(--bg-muted)' }}>
            <X size={16} style={{ color: 'var(--text-secondary)' }} />
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}

        {/* Status */}
        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Mapping Status</label>
          <div className="flex gap-2">
            {(['unmapped', 'in_progress', 'mapped'] as const).map(s => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className="flex-1 rounded-lg border py-2 text-xs font-medium transition-all"
                style={{
                  borderColor: status === s ? 'var(--primary-600, #2563eb)' : 'var(--border-primary)',
                  background: status === s ? 'var(--primary-50, #eff6ff)' : 'var(--bg-surface)',
                  color: status === s ? 'var(--primary-700, #1d4ed8)' : 'var(--text-secondary)',
                }}
              >
                {formatStatus(s)}
              </button>
            ))}
          </div>
        </div>

        {/* PO Search */}
        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Link Purchase Order</label>
          <div className="relative mb-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by PO number or vendor..."
              className="w-full rounded-lg border py-2 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ink-0)]"
              style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
            />
          </div>

          {/* None option */}
          <button
            onClick={() => setSelectedPoId(null)}
            className="mb-1 w-full rounded-lg border px-3 py-2 text-left text-sm transition-all"
            style={{
              borderColor: selectedPoId === null ? 'var(--primary-600, #2563eb)' : 'var(--border-primary)',
              background: selectedPoId === null ? 'var(--primary-50, #eff6ff)' : 'var(--bg-surface)',
              color: 'var(--text-secondary)',
            }}
          >
            — No PO assigned
          </button>

          <div className="max-h-48 overflow-y-auto rounded-lg border" style={{ borderColor: 'var(--border-primary)' }}>
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs" style={{ color: 'var(--text-muted)' }}>No purchase orders found</p>
            ) : (
              filtered.map(po => (
                <button
                  key={po.id}
                  onClick={() => setSelectedPoId(po.id)}
                  className="flex w-full items-center justify-between border-b px-3 py-2.5 text-left text-sm transition-all last:border-b-0"
                  style={{
                    borderColor: 'var(--border-primary)',
                    background: selectedPoId === po.id ? 'var(--primary-50, #eff6ff)' : 'var(--bg-surface)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <div>
                    <span className="font-medium">{po.number}</span>
                    {po.vendor?.name && (
                      <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>{po.vendor.name}</span>
                    )}
                  </div>
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                    style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)' }}
                  >
                    {po.status}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
            style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

